import moment from 'moment'
import React, { CSSProperties, useCallback, useEffect, useRef, useState } from 'react'
import { Button, Dropdown, DropdownProps, Form, Header, Icon, Image, Message, Modal } from 'semantic-ui-react'
import multiple_button from '../../../assets/svg/Multiple_Button.svg'
import DatepickerComponent from '../../../components/Datepicker/Datepicker'
import CircularLabel from '../../../components/Labels/CircularLabel'
import { _MIXEDCOLORS } from '../../../constants/colors'
import { createInitials } from '../../../utils/createInitials'
import { taskDisplayName } from '../../../utils/formatters'
import { IEmployee } from '../../settings/types/employee'
import {
  ITimeTracking,
  ITimeTrackingDirectCost,
  ITimeTrackingProject,
  ITimeTrackingProjectActivity
} from '../types/timeTracking'
import './TimeTrackingModal.css'
import { sortTasks, sortTasksByKey } from "../../../utils/sort";

const topDropdownStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  minWidth: '99px',
  minHeight: '32px',
  borderRadius: '8px',
  fontWeight: '600',
  letterSpacing: '0.25px',
  backgroundColor: '#F3F3F3',
  height: '32px',
  borderColor: '#F3F3F3',
  color: '#172533',
  padding: '8px 16px 8px 16px'
}

const bodyDropdownStyle: CSSProperties = {
  display: 'flex',
  boxSizing: 'border-box',
  height: '44px',
  width: '100%',
  border: '1px solid #E5EBF0',
  borderRadius: '4px',
  backgroundColor: '#FFFFFF',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '11px 9px 11px 14px',
  flexDirection: 'row',
  marginBottom: '21px'
}

const arrowDownStyle: CSSProperties = {
  color: '#08D6A1',
  fontSize: '19px',
  textAlign: 'right',
  top: '8px'
}

const entryTypeOptionsCreate = [
  { key: 1, value: 'today', text: 'Today' },
  { key: 2, value: 'single', text: 'Select Date' },
  { key: 3, value: 'multiple', text: 'Multiple' },
]

const entryTypeOptionsEdit = [
  { key: 1, value: 'today', text: 'Today' },
  { key: 2, value: 'single', text: 'Select Date' },
]

interface Props {
  projects: ITimeTrackingProject[]
  entry?: ITimeTracking
  open: boolean
  employee: IEmployee
  date?: Date
  entriesOfDate?: ITimeTracking[]
  totalDuration?: moment.Duration
  closeModal: (open: boolean) => void
  saveTimeTrackingEntry: (entry: ITimeTracking) => void
  getEmployeeColor?: (employee: IEmployee) => string
}

const renderAvatar = (emp: IEmployee, getColor?: (employee: IEmployee) => string) => {
  const getEmployeeColor = (employee: IEmployee): string => {
    if (employee.color) return employee.color
    if (getColor) return getColor(employee)
    // Default fallback to random color
    const randColor = Math.floor((Math.random() * (_MIXEDCOLORS.length - 1) + 1))
    return _MIXEDCOLORS[randColor]
  }
  
  return emp.photo ?
    <Image
      circular={true}
      src={emp.photo}
      style={{ width: '42.63px', height: '42.63px', minWidth: '44px', minHeight: '44px', objectFit: 'cover' }}
    /> : <CircularLabel
      width='42.63px'
      height='42.63px'
      style={{ fontSize: '1rem', marginRight: '8.68px' }}
      content={createInitials(emp)}
      color={getEmployeeColor(emp)}
    />
}

const TimeTrackingModal = (props: Props): JSX.Element => {
  const {
    projects,
    open,
    entry,
    employee,
    date,
    totalDuration,
    entriesOfDate,
    closeModal,
    saveTimeTrackingEntry,
    getEmployeeColor
  } = props

  const _initialEntry: ITimeTracking = {
    id: '',
    employee: employee,
    activity: {} as ITimeTrackingProjectActivity,
    direct_cost: {} as ITimeTrackingDirectCost,
    date: date ? date : new Date(),
    duration: '',
    inputDuration: '',
    project_name: '',
    project_color: '',
    entryType: date ? 'single' : 'today',
  }
  const [timeTrackingEntry, setTimeTrackingEntry] = useState(entry ? entry : _initialEntry)

  const initialProjects = () => {
    return projects
      .map((p) => {
        return {
          key: p.id.toString(),
          value: p.id.toString(),
          text: p.name,
        }
      })
      .sort((a, b) => a.text.localeCompare(b.text))
  }

  const [entryType, setEntryType] = useState(date && date.getDate() !== new Date().getDate() ? 'single' : 'today')
  const [selectedProject, setSelectedProject] = useState('')
  const [activities, setTasks] = useState<{ key: string, value: string, text: string }[]>([])
  const [selectedActivity, setSelectedActivity] = useState('')
  const [openDatePicker, setOpenDatePicker] = useState(false)
  const [multiDates, setMultiDates] = useState<[null | Date, null | Date]>([entry ? new Date(entry.date) : new Date(_initialEntry.date), null])
  const [_inputHours, setInputHours] = useState(
    entry ? (entry.duration.toString().split(':')[0] === '1 00' ? '24' : entry.duration.toString().split(':')[0]) : '00'
  )
  const [_inputMinutes, setInputMinutes] = useState(entry ? entry.duration.toString().split(':')[1] : '00')
  const [msgShow, setMsgShow] = useState(totalDuration && totalDuration >= moment.duration(1, 'days'))
  const [hoursCursor, setHoursCursor] = useState(-1)
  const [minCursor, setMinCursor] = useState(-1)
  const [defaultDate, setDefaultDate] = useState(entry ? new Date(entry.date) : new Date(_initialEntry.date))

  const _minInputElRef = useRef<HTMLInputElement | null>(null)
  const _hoursInputElRef = useRef<HTMLInputElement | null>(null)

  const updateActDropdownState = useCallback(
    (selectedPr: string) => {
      const filteredPr = projects.find((p) => selectedPr && p.id.toString() === selectedPr.toString())
      if (filteredPr) {
        if (filteredPr.type === 'service_provider' && filteredPr.direct_costs) {
          const _dcs = sortTasksByKey(filteredPr.direct_costs, 'key', 'offer_position')
            .map((dc) => {
              return {
                key: dc.id.toString(),
                value: dc.id.toString(),
                // Merged label so the key is visible and searchable in the picker (#89).
                text: taskDisplayName(dc.key, dc.offer_position),
              }
            })
          // Task of an existing entry may have been dropped from the current offer version
          // (kept only because hours are booked) - keep it selectable so the label doesn't go blank.
          const entryDc = entry?.direct_cost
          if (entryDc?.project?.toString() === selectedPr.toString() && !_dcs.some((o) => o.value === entryDc.id.toString())) {
            _dcs.push({ key: entryDc.id.toString(), value: entryDc.id.toString(), text: entryDc.offer_position })
          }
          setTasks(_dcs)
        } else if (filteredPr.activities) {
          const _acts = sortTasks(filteredPr.activities, 'name')
            .map((a) => {
              return {
                key: a.id.toString(),
                value: a.id.toString(),
                text: a.name,
              }
            })
          setTasks(_acts)
        }
      }
    },
    [projects, entry]
  )

  useEffect(() => {
    if (entry) {
      const { activity, direct_cost, project_name } = entry
      setSelectedProject(
        activity && activity.project
          ? activity.project.toString()
          : direct_cost && direct_cost.project
            ? direct_cost.project.toString()
            : ''
      )
      setSelectedActivity(activity && activity.project ? activity.id.toString() : direct_cost?.id ? direct_cost.id.toString() : '')
      setTimeTrackingEntry(entry)
      setMultiDates([new Date(entry.date), null])
      setDefaultDate(new Date(entry.date))
    }
  }, [entry])

  useEffect(() => {
    const _date_entries = entriesOfDate
      ? entriesOfDate.filter((entry) => new Date(entry.date).getDate() === new Date(timeTrackingEntry.date).getDate())
      : []
    const dailyWorkload = _date_entries
      .map((e) => moment.duration(e.duration).asHours())
      .reduce((acc, val) => acc + val, 0)
    const _totalDurationDate = moment
      .duration(dailyWorkload, 'hours')
      .subtract(moment.duration(timeTrackingEntry.duration))
    const _durationHours =
      _totalDurationDate >= moment.duration(1, 'days') ? 24 : _totalDurationDate ? _totalDurationDate.hours() : 0
    const _durationMinutes = _totalDurationDate ? _totalDurationDate.minutes() : 0
    const _hours = +_inputHours + _durationHours
    const _minutes = +_inputMinutes + _durationMinutes
    const _totalMomentDuration = moment.duration(_hours, 'hours').add(moment.duration(_minutes, 'minutes'))

    if (_totalMomentDuration > moment.duration(1, 'days')) {
      if (
        timeTrackingEntry &&
        timeTrackingEntry.id !== '' &&
        +_inputHours <= +timeTrackingEntry.duration.toString().split(':')[0] &&
        timeTrackingEntry &&
        timeTrackingEntry.id !== '' &&
        +_inputMinutes <= +timeTrackingEntry.duration.toString().split(':')[1]
      ) {
        setMsgShow(false)
      } else {
        setMsgShow(true)
      }
    } else {
      setMsgShow(false)
    }
  }, [_inputHours, _inputMinutes, entriesOfDate, timeTrackingEntry])

  useEffect(() => {
    if (selectedProject) {
      updateActDropdownState(selectedProject)
    }
  }, [selectedProject, projects, updateActDropdownState])

  useEffect(() => {
    const input = _hoursInputElRef.current
    if (input) {
      input.setSelectionRange(hoursCursor, hoursCursor)
    }
  }, [_hoursInputElRef, hoursCursor, _inputHours])

  useEffect(() => {
    const input = _minInputElRef.current
    if (input) {
      input.setSelectionRange(minCursor, minCursor)
    }
  }, [_minInputElRef, minCursor, _inputMinutes])

  const onInputClick = () => {
    setOpenDatePicker(!openDatePicker)
  }

  const changeDate = (date: Date | [Date, Date] | null) => {
    if (Array.isArray(date) && entryType === 'multiple') {
      const [start, end] = date
      setMultiDates([start, end])
      setTimeTrackingEntry((prev) => ({
        ...prev,
        startDate: moment(start).format('YYYY-MM-DD'),
        endDate: moment(end).format('YYYY-MM-DD'),
      }))
      if (end) {
        setOpenDatePicker(false)
      }
    } else {
      setTimeTrackingEntry((prev) => ({ ...prev, date: date as Date }))
      setOpenDatePicker(false)
    }
  }

  const handleEntryTypeChange = (e: React.SyntheticEvent<HTMLElement, Event>, data: DropdownProps) => {
    if (data.value) {
      const _val = data.value.toString()
      setEntryType(_val)
      if (_val === 'multiple' || _val === 'single') {
        setTimeTrackingEntry((prev) => ({ ...prev, date: defaultDate, entryType: data.value?.toString() }))
        setOpenDatePicker(true)
      } else {
        setTimeTrackingEntry((prev) => ({ ...prev, date: new Date(), entryType: data.value?.toString() }))
      }
    }
  }

  const handleProjectChange = (e: React.SyntheticEvent<HTMLElement, Event>, data: DropdownProps) => {
    if (data.value) {
      setSelectedProject(data.value.toString())
      setSelectedActivity('')
      setTimeTrackingEntry({ ...timeTrackingEntry, direct_cost: {} as ITimeTrackingDirectCost, activity: {} as ITimeTrackingProjectActivity })
      updateActDropdownState(data.value.toString())
    } else {
      setSelectedProject('')
      setTasks([])
    }
  }

  const handleActivityChange = (e: React.SyntheticEvent<HTMLElement, Event>, data: DropdownProps) => {
    if (data.value) {
      setSelectedActivity(data.value.toString())
      const pr = projects.find((p) => p.id.toString() === selectedProject.toString())
      if (pr) {
        if (pr.type === 'service_provider') {
          const _cost = pr.direct_costs?.find((a) => a.id.toString() === data.value?.toString())
          if (_cost) {
            setTimeTrackingEntry((prev) => ({ ...prev, activity: {} as ITimeTrackingProjectActivity, direct_cost: _cost }))
          }
        } else {
          const _act = pr.activities?.find((a) => a.id.toString() === data.value?.toString())
          if (_act) {
            setTimeTrackingEntry((prev) => ({ ...prev, activity: _act, direct_cost: {} as ITimeTrackingDirectCost}))
          }
        }
      }
    } else {
      setSelectedActivity('')
    }
  }

  const handleDurationHoursChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setInputHours((min) => {
      let _duration = min
      const minuteElement = document.getElementById('minute')
      if (event.target.value === '') {
        _duration = ''
      } else {
        if (event.target.selectionEnd) {
          const idx = event.target.selectionEnd - 1
          if (event.target.value.length - 1 < 3) {
            _duration = setCharAt(_duration, idx, event.target.value[idx])
          }
        }
      }

      if (
        Number(event.target.value) > 2 &&
        !_duration.startsWith('0') &&
        !_duration.startsWith('1') &&
        !_duration.startsWith('2')
      ) {
        _duration = '0' + event.target.value
        minuteElement?.focus() ?? ''
      }

      if (event.target.value.length - 1 === 2) {
        minuteElement?.focus() ?? ''
      }

      if (+_duration > 24) {
        _duration = '24'
      }
      return _duration
    })
    if (event.target.selectionStart) {
      setHoursCursor(event.target.selectionStart)
      if (event.target.selectionStart >= 3) {
        if (_minInputElRef && _minInputElRef.current) {
          _minInputElRef.current.focus()
          _minInputElRef.current.setSelectionRange(0, _minInputElRef.current.value.length)
        }
      }
    }
  }

  function setCharAt(current: string, index: number, replace: string) {
    return index > current.length - 1 ? current : current.substring(0, index) + replace + current.substring(index + 1)
  }

  const handleDurationMinChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setInputMinutes((min) => {
      let _duration = min
      if (event.target.value === '') {
        _duration = ''
      } else {
        if (event.target.selectionEnd) {
          const idx = event.target.selectionEnd - 1
          if (event.target.value.length - 1 < 3) {
            _duration = setCharAt(_duration, idx, event.target.value[idx])
          }
        }
      }

      if (+_duration > 59) {
        _duration = '59'
      }
      return _duration
    })
    if (event.target.selectionStart) {
      setMinCursor(event.target.selectionStart)
    }
  }

  const disableSave = () => {
    const _m = _inputMinutes === '' || _inputMinutes === '0' ? '00' : _inputMinutes
    const _h = _inputHours === '0' ? '00' : _inputHours
    const validRegExp =
      _inputHours !== '24'
        ? RegExp(/^([01]?[0-9]|2[0-4])h ([09]?[0-9]|[0-5][0-9])m$/).test(`${_h}h ${_m}m`)
        : _inputMinutes !== '00'
          ? false
          : true
    const validateMinEntry = _h === '00' && _m === '00' ? false : true
    if (selectedProject !== '' && selectedActivity !== '' && validRegExp && validateMinEntry && !msgShow) {
      if (entryType === 'multiple' && (!timeTrackingEntry.endDate || timeTrackingEntry.endDate === 'Invalid date')) {
        return true
      }
      return false
    } else {
      return true
    }
  }

  const onClose = () => {
    setSelectedProject('')
    setSelectedActivity('')
    closeModal(false)
  }

  const handleMultiEntryMode = () => {
    setEntryType('multiple')
    setTimeTrackingEntry((prev) => ({ ...prev, entryType: 'multiple' }))
    setOpenDatePicker(true)
  }

  const handleHourKeyPress = (event: React.KeyboardEvent) => {
    const key = event.key
    const minuteElement = document.getElementById('minute')
    const hourElement = document.getElementById('hour') as HTMLInputElement
    let hourElementValue = hourElement?.value ?? ''

    key === 'Enter' ? event.preventDefault() : undefined
    const isNotNumber = key === ' ' || isNaN(Number(key))
    if (isNotNumber) {
      if (hourElementValue.startsWith('1') || hourElementValue.startsWith('2')) {
        if (key !== 'ArrowLeft' && key !== 'ArrowRight') {
          event.preventDefault()
        }
        hourElementValue = hourElementValue.replace("0", "")
        if (hourElementValue.length < 2) {
          hourElementValue = "0" + hourElementValue
        }
        setInputHours(hourElementValue)
        minuteElement?.focus() ?? ''
      }
    }
  }

  const handleMinuteKeyPress = (event: React.KeyboardEvent) => {
    const key = event.key
    key === 'Enter' ? event.preventDefault() : undefined
    const isNotNumber = key === ' ' || isNaN(Number(key))
    if (isNotNumber && key !== 'ArrowLeft' && key !== 'ArrowRight') {
      event.preventDefault()
    }
  }

  const handleClickOnHourDiv = () => {
    const hourElement = document.getElementById('hour')
    hourElement?.focus() ?? ''
  }

  const handleClickOnMinuteDiv = () => {
    const minuteElement = document.getElementById('minute')
    minuteElement?.focus() ?? ''
  }

  const saveEntry = () => {
    const _m = _inputMinutes.length === 0 ? '00' : _inputMinutes.length === 1 ? `0${_inputMinutes}` : _inputMinutes
    const _cleanedEntry = { ...timeTrackingEntry }
    _cleanedEntry.date = moment(_cleanedEntry.date).format('YYYY-MM-DD')
    _cleanedEntry.duration = moment.duration(`${_inputHours}:${_m}`).asHours()
    _cleanedEntry.inputDuration = `${_inputHours}h ${_m}m`
    saveTimeTrackingEntry(_cleanedEntry)
    closeModal(false)
  }

  const handleDurationFocus = (event: React.ChangeEvent<HTMLInputElement>) => {
    event.target.select()
  }
  const renderNotificationForDuration = () => {
    if (msgShow) {
      return (
        <Message
          error
          header='Duration notification'
          content='The entered time span leads to the 24 hours per day limit being exceeded. Please enter a shorter time span.'
        />
      )
    }
    return null
  }

  const renderDatePicker = () => {
    if (entryType === 'single') {
      return (
        <React.Fragment>
          <label style={{ fontSize: '18px', fontWeight: 600, marginBottom: '8px' }}>Select Date</label>
          <DatepickerComponent
            inputBorder={true}
            open={openDatePicker}
            onClickOutside={() => setOpenDatePicker(false)}
            onInputClick={() => onInputClick()}
            onChange={(date: Date) => changeDate(date)}
            selected={timeTrackingEntry.date ? moment(timeTrackingEntry.date).toDate() : undefined}
            dateFormat='dd.MM.yyyy'
          />
        </React.Fragment>
      )
    } else if (entryType === 'multiple') {
      const value = {
        startDate: multiDates[0] ? moment(multiDates[0]).format('DD.MM.YYYY') : '',
        endDate: multiDates[1] ? moment(multiDates[1]).format('DD.MM.YYYY') : '',
      }
      return (
        <React.Fragment>
          <label style={{ fontSize: '18px', fontWeight: 600, marginBottom: '8px' }}>Add Range</label>
          <DatepickerComponent
            inputBorder={true}
            open={openDatePicker}
            onClickOutside={() => setOpenDatePicker(false)}
            onInputClick={() => onInputClick()}
            onChange={(date) => changeDate(date)}
            startDate={multiDates[0] ? moment(multiDates[0]).toDate() : null}
            endDate={multiDates[1] ? moment(multiDates[1]).toDate() : null}
            selectsRange={true}
            dateFormat='dd.MM.yyyy'
            customInput={
              value.startDate !== '' ? (
                <div style={{ color: '#172533' }}>{`${value.startDate} - ${value.endDate}`}</div>
              ) : (
                <div>
                  <p style={{ opacity: 0.4 }}>Set a date range</p>
                </div>
              )
            }
          />
        </React.Fragment>
      )
    } else {
      return null
    }
  }

  return (
    <Modal style={{ width: '35rem' }} closeOnEscape={false} open={open} onClose={onClose}>
      <div style={{ display: 'flex', flexDirection: 'row', padding: '42px 40px 0px 38px', justifyContent: 'space-between' }}>
        <Header style={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
          {renderAvatar(employee, getEmployeeColor)}
          {timeTrackingEntry.id !== '' ? 'Edit Time Entry' : 'Time Entry'}</Header>
        <Dropdown
          floating={true}
          className='blue-dropdown blue-dropdown-custom-padding'
          icon={<Icon name='caret down' style={{ fontSize: '16px', marginRight: '-.5rem', lineHeight: 'initial', marginTop: '-4px' }} />}
          style={topDropdownStyle}
          onChange={handleEntryTypeChange}
          options={timeTrackingEntry.id === '' ? entryTypeOptionsCreate : entryTypeOptionsEdit}
          selection={true}
          value={entryType}
        />
      </div>
      <Modal.Content style={{ padding: '0 40px 0 40px' }}>
        <Form>
          <Form.Field>
            <label style={{ fontSize: '18px', letterSpacing: '0.25px', fontWeight: 600, marginBottom: '8px', marginTop: '10px' }}>Project</label>
            <Dropdown
              floating={true}
              icon={<Icon className='dropdown custom-button' name='caret down' style={arrowDownStyle} />}
              placeholder='Select Project'
              onChange={handleProjectChange}
              options={initialProjects()}
              selection={true}
              search={true}
              value={selectedProject}
              clearable={true}
              style={bodyDropdownStyle}
            />
          </Form.Field>
          <Form.Field>
            <label style={{ fontSize: '18px', fontWeight: 600, marginBottom: '8px' }}>Task</label>
            <Dropdown
              icon={<Icon className='dropdown custom-button' name='caret down' style={arrowDownStyle} />}
              floating={true}
              placeholder='Enter Task'
              onChange={handleActivityChange}
              options={activities}
              selection={true}
              search={true}
              value={selectedActivity}
              clearable={true}
              style={bodyDropdownStyle}
            />
          </Form.Field>
          <Form.Field width='8' style={{ marginBottom: '21px' }}>
            <label style={{ fontSize: '18px', fontWeight: 600, marginBottom: '8px' }}>Duration</label>
            <Form.Group style={{ marginLeft: '0rem' }}>
              <div className='inputDuration' style={{ width: '106px', height: '32px', marginRight: '6px', display: 'flex', alignItems: 'baseline', paddingLeft: '9px' }}>
                <input
                  type='text'
                  id='hour'
                  style={{ width: '18px', height: '2.3rem', background: 'none', paddingLeft: 0, paddingRight: 0 }}
                  value={_inputHours}
                  onChange={(e) => handleDurationHoursChange(e)}
                  placeholder={'00h'}
                  ref={_hoursInputElRef}
                  onKeyDown={handleHourKeyPress}
                  onFocus={handleDurationFocus}
                />
                <div
                  style={{
                    userSelect: 'none',
                    zIndex: 1,
                    fontWeight: 'normal',
                    height: '16px',
                    width: 'min-content',
                  }}
                  onClick={handleClickOnHourDiv}
                >
                  {'h'}
                </div>
                <input
                  type='text'
                  id='minute'
                  style={{ width: '18px', height: '2.3rem', background: 'none', paddingLeft: 0, paddingRight: 0, marginLeft: '6px' }}
                  value={_inputMinutes}
                  onChange={handleDurationMinChange}
                  placeholder={'00m'}
                  ref={_minInputElRef}
                  onKeyDown={handleMinuteKeyPress}
                  onFocus={handleDurationFocus}
                />
                <div
                  style={{
                    userSelect: 'none',
                    zIndex: 1,
                    fontWeight: 'normal',
                    height: '16px',
                    width: 'min-content',
                  }}
                  onClick={handleClickOnMinuteDiv}
                >
                  {'m'}
                </div>
              </div>
              {timeTrackingEntry.id === '' && <Image
                src={multiple_button}
                onClick={handleMultiEntryMode}
              />}
            </Form.Group>
          </Form.Field>
          <Form.Field width='8'>{renderDatePicker()}</Form.Field>
        </Form>
        {renderNotificationForDuration()}
      </Modal.Content>
      <Modal.Actions
        style={{
          backgroundColor: '#F9FBFC',
          height: '64px',
          borderRadius: '0 0 6.46px 6.46px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
          marginTop: '42px',
          paddingRight: '40px'
        }}>
        <Button
          style={{
            borderRadius: '4.8px',
            height: '24px',
            width: '100px',
            border: '1px solid #BEC8C3',
            backgroundColor: ' #FFFFFF',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontWeight: '600',
            boxShadow: 'none',
            color: '#747678'
          }}
          basic
          onClick={() => closeModal(false)}
          content='Clear' />
        <Button
          disabled={disableSave()}
          style={{
            borderRadius: '4.8px',
            height: '24px',
            width: '100px',
            backgroundColor: '#08D6A1',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
          primary
          color='green'
          onClick={saveEntry}
          content='Save'
        />
      </Modal.Actions>
    </Modal>
  )
}

export default TimeTrackingModal
